/**
 * Canonical status chip for UDE flows.
 *
 * The full state→color taxonomy lives in DESIGN.md §11.9. Every screen
 * that surfaces a UDE status must use this component so the mapping
 * stays consistent.
 */
import {Chip, ChipProps} from '@mui/material';
import {ProjectStatus, ExtractionStatus, Confidence} from '../../api/ude';

type DocumentStatus = 'pending' | 'uploading' | 'classifying' | 'classified' | 'failed' | 'user_override';
type ComplianceVerdict = 'pass' | 'fail' | 'cant_determine' | 'not_applicable';

type AnyStatus = ProjectStatus | ExtractionStatus | DocumentStatus | ComplianceVerdict | Confidence;

interface StatusChipProps extends Omit<ChipProps, 'color' | 'label'> {
    status: AnyStatus;
    /** Override the auto-generated label (e.g. for translation). */
    label?: string;
}

// Norwegian labels — fall back to English token if not mapped.
const NB_LABELS: Partial<Record<AnyStatus, string>> = {
    // Project (derived)
    empty: 'Tom',
    uploaded: 'Lastet opp',
    extracted: 'Uthentet',
    checked: 'Kontrollert',
    // Extraction run
    pending: 'Venter',
    running: 'Kjører',
    completed: 'Fullført',
    failed: 'Feilet',
    // Document classification
    uploading: 'Laster opp',
    classifying: 'Klassifiserer',
    classified: 'Klassifisert',
    user_override: 'Overstyrt',
    // Compliance verdicts
    pass: 'Godkjent',
    fail: 'Avvik',
    cant_determine: 'Kan ikke avgjøre',
    not_applicable: 'Ikke aktuell',
    // Confidence
    high: 'Høy',
    medium: 'Middels',
    low: 'Lav',
};

const COLOR_MAP: Record<AnyStatus, ChipProps['color']> = {
    // Project (derived) — neutral until something has happened
    empty: 'default',
    uploaded: 'primary',       // documents uploaded + classified
    extracted: 'success',      // facts produced
    checked: 'success',        // compliance run; could split later if we add a "fail" project state
    // Extraction run
    pending: 'default',
    running: 'info',
    completed: 'success',
    failed: 'error',
    // Document classification
    uploading: 'info',
    classifying: 'info',
    classified: 'primary',
    // failed already mapped above
    user_override: 'secondary',
    // Compliance verdicts (decision 9 — cant_determine is NEVER red)
    pass: 'success',
    fail: 'error',
    cant_determine: 'default',
    not_applicable: 'default',
    // Confidence
    high: 'success',
    medium: 'warning',
    low: 'error',
};

export default function StatusChip({status, label, sx, size = 'small', ...rest}: StatusChipProps) {
    const color = COLOR_MAP[status] ?? 'default';
    const text = label ?? NB_LABELS[status] ?? status;
    const isNotApplicable = status === 'not_applicable';

    return (
        <Chip
            label={text}
            color={color}
            size={size}
            sx={{
                fontWeight: 500,
                opacity: isNotApplicable ? 0.6 : 1,
                ...sx,
            }}
            {...rest}
        />
    );
}
